import { z } from 'zod';
import type { UserProfileDto } from '@/api/generated/types.gen';
import { emailSchema, fileArraySchema } from '@/shared/validation/common.schemas';
import { esteTelefonNational, EXEMPLU_TELEFON, telefonNational } from '@/shared/utils/telefon-national';

/**
 * Profile validation schemas - Role-specific
 *
 * Uses Context7-validated patterns:
 * - Conditional schemas based on user role
 * - fileArraySchema() for the collaboration protocol (any file type, owner 21.09)
 * - Reuses common schemas (emailSchema)
 * - Type safety with UserProfileDto
 */

/**
 * Telefonul din profil, in forma pe care o cere backendul: `UserProfileDTO.telefon` are
 * `@Pattern("\d{10}")`.
 *
 * 🔴 Aici era `phoneSchema`, care accepta «+», spatii si paranteze: «+40 722 123 456» trecea de
 * formular si primea 400 de la server (07.10). Acelasi remediu ca la comanda de recipiente
 * (f5b6ab83): valoarea e adusa la forma nationala, apoi verificata exact ca pe server, deci un
 * numar gresit e semnalat sub camp, inainte de trimitere.
 *
 * `.transform`, nu `preprocess`: intrarea ramane `string`, deci `satisfies z.ZodType<…>` de mai jos
 * tine. `null` de la server nu ajunge aici — `toProfileFormValues` il face sir gol.
 */
const telefonProfilSchema = z
  .string()
  .min(1, 'Numărul de telefon este obligatoriu')
  .transform((telefon) => telefonNational(telefon))
  .refine(esteTelefonNational, {
    message: `Numărul de telefon trebuie să aibă exact 10 cifre. Exemplu: ${EXEMPLU_TELEFON}.`,
  });

// Helper: Class value validation (for ELEV). Aceleasi valori ca `constants/classes.ts` si ca
// `UserService.getSchoolClasses()` pe server; IX-XII din 2026-10-24 (liceele se inscriu din 18.09).
export const classValueSchema = z.enum(['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'], {
  errorMap: () => ({ message: 'Clasa selectată este invalidă' }),
});

/**
 * PROFESOR profile schema
 * Fields: nume, telefon, protocolDeColaborare
 * Readonly fields handled in component: codProfesor, email, codSe
 */
export const profesorProfileSchema = z.object({
  nume: z.string().min(1, 'Numele este obligatoriu'),
  telefon: telefonProfilSchema,
  email: emailSchema, // Readonly in component
  codProfesor: z.string().optional(), // Readonly in component
  codSe: z.string().optional(), // Readonly in component
  protocolDeColaborare: fileArraySchema(0, 5), // 0-5 fisiere, orice tip (owner 21.09)
}) satisfies z.ZodType<Partial<UserProfileDto>>;

/**
 * ELEV profile schema
 * Fields: nume (nickname), clasa
 * Readonly fields handled in component: codSe, email
 */
export const elevProfileSchema = z.object({
  nume: z.string().min(1, 'Nickname-ul este obligatoriu'),
  clasa: classValueSchema,
  email: emailSchema, // Readonly in component
  codSe: z.string().optional(), // Readonly in component
}) satisfies z.ZodType<Partial<UserProfileDto>>;

/**
 * ADMIN profile schema
 * Fields: nume, telefon
 * Readonly fields handled in component: email
 */
export const adminProfileSchema = z.object({
  nume: z.string().min(1, 'Numele este obligatoriu'),
  telefon: telefonProfilSchema,
  email: emailSchema, // Readonly in component
}) satisfies z.ZodType<Partial<UserProfileDto>>;

/**
 * Inferred TypeScript types from schemas
 */
export type ProfesorProfileData = z.infer<typeof profesorProfileSchema>;
export type ElevProfileData = z.infer<typeof elevProfileSchema>;
export type AdminProfileData = z.infer<typeof adminProfileSchema>;

/**
 * Union type for all profile forms
 */
export type ProfileFormData = ProfesorProfileData | ElevProfileData | AdminProfileData;

/**
 * Forma pe care o vede FORMULARUL, nu cea pe care o valideaza schema.
 *
 * Schema se alege la runtime, dupa rol, deci TypeScript nu poate sti care dintre cele
 * trei e activa. O uniune n-ar merge: `form.control` ar deveni inutilizabil, si de
 * asta codul capitulase la `useForm<any>` — care sterge verificarea pe TOT formularul.
 *
 * Aici pastram numele de campuri verificate (restul e ce conteaza in componenta) si
 * marcam optionale campurile care apar doar la anumite roluri. Validarea reala ramane
 * a schemei, la runtime.
 */
export type ProfileFormValues = {
    nume: string;
    email: string;
    telefon?: string;
    codProfesor?: string;
    codSe?: string;
    clasa?: z.infer<typeof classValueSchema>;
    protocolDeColaborare?: ProfesorProfileData['protocolDeColaborare'];
};

/**
 * UserProfileDto -> valorile formularului.
 *
 * DTO-ul are 15+ campuri, formularul foloseste 7. Inainte se dadea DTO-ul intreg lui
 * `form.reset()`, ceea ce mergea doar pentru ca formularul era `any`. Acum alegem
 * explicit, si campurile lipsa devin sir gol in loc de `undefined` — RHF cere
 * componente controlate.
 */
export function toProfileFormValues(dto: UserProfileDto | undefined): ProfileFormValues {
    return {
        nume: dto?.nume ?? '',
        email: dto?.email ?? '',
        // `telefonNational` primeste si `null` (tipul generat minte: `string | undefined`) si
        // arata numarul stocat ca «+40…» in forma care va fi trimisa.
        telefon: telefonNational(dto?.telefon),
        codProfesor: dto?.codProfesor ?? '',
        codSe: dto?.codSe ?? '',
        clasa: classValueSchema.safeParse(dto?.clasa).data,
        protocolDeColaborare: dto?.protocolDeColaborare?.filter(
            (url): url is string => typeof url === 'string'
        ),
    };
}

/**
 * Helper function to get schema based on role
 */
export function getProfileSchemaForRole(role: string) {
  switch (role) {
    case 'PROFESOR':
      return profesorProfileSchema;
    case 'ELEV':
      return elevProfileSchema;
    case 'ADMIN':
      return adminProfileSchema;
    default:
      return adminProfileSchema; // Fallback
  }
}

/**
 * Construieste corpul lui `PATCH /api/users/profile` din starea BRUTA a formularului.
 *
 * 🔴 De ce exista (issue #14): `defaultValues` pune MEREU `telefon: ''` si `codProfesor: ''` in
 * starea formularului, indiferent de rol. Pentru ELEV, campul `telefon` nici macar nu se randeaza
 * (`ProfilePage.tsx:219`), dar valoarea ramanea in stare, iar pagina trimitea
 * `form.getValues()` **brut**, ocolind schema. Backendul are `@Pattern("\d{10}")` pe `telefon`,
 * care accepta `null` dar **respinge sirul gol** → 400 pe un camp pe care elevul nu-l putea vedea
 * si nici completa. **Toti cei 34.332 de elevi** erau afectati; niciunul nu-si putea salva
 * profilul, nici macar nickname-ul.
 *
 * Fixul nu inventeaza nimic: trece valorile prin schema rolului, care oricum era sursa de adevar.
 * `z.object()` **elimina cheile necunoscute** la parsare, deci `elevProfileSchema` scoate singur
 * `telefon` si `codProfesor` — exact campurile pe care elevul nu le are.
 *
 * ⚠️ Se apeleaza DUPA `form.trigger()`, deci parsarea trece prin definitie. Daca totusi esueaza,
 * intoarce `null` si apelantul NU trimite nimic — mai bine o salvare care nu se intampla decat una
 * cu un corp construit din valori nevalidate.
 */
export function buildProfilePayload(role: string, rawValues: unknown): Record<string, unknown> | null {
  const rezultat = getProfileSchemaForRole(role).safeParse(rawValues);
  return rezultat.success ? (rezultat.data as Record<string, unknown>) : null;
}
